<template>
  <div class="mdlist-panel">
    <div v-if="mdList">
      <div class="mdlist-header">
        <strong class="title">Template: {{ mdList.name }}</strong>

        <div class="header-actions">
          <template v-if="!isEditingAll">
            <button class="btn-ghost" @click="enableEditAll" title="Edit all values">
              <i class="bi bi-pencil"></i>
              <span class="btn-text">Edit all</span>
            </button>
          </template>
          <template v-else>
            <button class="btn" @click="saveAllMetadata">Save</button>
            <button class="btn-secondary" @click="cancelEditAll">Cancel</button>
          </template>

          <!-- Delete MDList -->
          <button class="btn-ghost danger" @click="deleteMdList" title="Remove template from entity">
            <i class="bi bi-trash"></i>
          </button>

          <!-- Add attribute button -->
          <button class="btn-ghost" @click="openAddKeyModal" title="Add new attribute">
            <i class="bi bi-plus-lg"></i>
            <span class="btn-text">Add attribute</span>
          </button>
        </div>
      </div>

      <div class="toolbar-row">
        <div class="non-default-row">
          <multiselect
            v-model="selectedNonDefaultKey"
            :options="nonDefaultKeys"
            :searchable="true"
            label="name"
            track-by="id"
            placeholder="Non-Default Attributes"
          ></multiselect>
          <button :disabled="!selectedNonDefaultKey" class="btn" @click="addExistingNonDefaultKey">Add</button>
        </div>
      </div>

      <!-- Metadata Items -->
      <ul class="kv-list">
        <li
          v-for="(item, index) in metadata"
          :key="itemKey(item)"
          class="metadata-item"
          @click="enableEdit(index)"
          :class="{ editable: !isEditingAll }"
        >
          <div class="editable-row">
            <strong class="metadata-key">{{ item.name }}:</strong>
            <span v-if="!(isEditingAll || item.isEditing)">
              <span class="metadata-value">
                {{ formatMetadataValue(item.value, item.value_type) }}
              </span>
              <span v-if="item.unit" class="metadata-unit">({{ item.unit }})</span>
            </span>
            <div v-else class="edit-row" @click.stop>
              <!-- Dropdown for boolean -->
              <select
                v-if="item.value_type === 'boolean'"
                :value="item.value"
                @input="item.value = $event.target.value"
                @keyup.enter="saveMetadata(index)"
                class="value-input"
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
              
              <!-- Date input -->
              <input
                v-else-if="item.value_type === 'date'"
                type="date"
                :value="item.value || ''"
                @input="item.value = $event.target.value"
                @keyup.enter="saveMetadata(index)"
                class="value-input"
              />
              
              <!-- DateTime input -->
              <input
                v-else-if="item.value_type === 'datetime'"
                type="datetime-local"
                :value="valueToInputFormat(item.value, item.value_type)"
                @input="item.value = inputToStorageFormat($event.target.value, item.value_type)"
                @keyup.enter="saveMetadata(index)"
                class="value-input"
              />
              
              <!-- Number input for integer/float -->
              <input
                v-else-if="item.value_type === 'integer' || item.value_type === 'float'"
                :type="item.value_type === 'float' ? 'text' : 'number'"
                :inputmode="item.value_type === 'float' ? 'decimal' : 'numeric'"
                :step="item.value_type === 'float' ? undefined : '1'"
                :value="item.value || ''"
                @input="item.value = item.value_type === 'float' ? normalizeFloatInput($event.target.value) : $event.target.value"
                @beforeinput="item.value_type === 'float' ? handleFloatBeforeInput($event, item) : null"
                @keydown="item.value_type === 'float' ? handleFloatKeydown($event, item) : null"
                @keyup.enter="saveMetadata(index)"
                class="value-input"
              />
              
              <!-- Text input (default) -->
              <input
                v-else
                type="text"
                :value="item.value === null ? '' : item.value"
                @input="item.value = $event.target.value"
                :placeholder="`Value (${getTypeLabel(item.value_type)})`"
                @keyup.enter="saveMetadata(index)"
                class="value-input"
              />
              <template v-if="!isEditingAll">
                <button @click.stop="saveMetadata(index)" class="save-btn">✔</button>
                <button @click.stop="cancelEdit(index)" class="cancel-btn">✖</button>
              </template>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div v-else class="mdlist-creation">
      <p class="message">No metadata list linked yet.</p>

      <div class="picker-row">
        <multiselect
          v-model="selectedMdListToAdd"
          :options="allMdLists"
          :searchable="true"
          label="name"
          track-by="id"
          placeholder="Templates"
          class="filter-multiselect"
        ></multiselect>
        <button
          class="btn"
          :disabled="!selectedMdListToAdd"
          @click="attachMdListToEntity"
        >
          Attach
        </button>
      </div>
    </div>

    <!-- Add Key/Value Modal -->
    <div v-if="showAddKeyModal" class="pl-modal-overlay" @click.self="closeAddKeyModal">
      <div class="pl-modal" role="dialog" aria-modal="true" aria-label="Add new attribute">
        <div class="pl-modal-header">
          <h4>Add new attribute</h4>
          <button class="pl-modal-close" @click="closeAddKeyModal">×</button>
        </div>
        <div class="pl-modal-body">
          <div class="form-row">
            <label>Key</label>
            <input type="text" v-model="newMetadata.key" placeholder="Attribute name" />
          </div>
          <div class="form-row">
            <label>Type</label>
            <select v-model="newMetadata.value_type">
              <option v-for="vt in valueTypes" :key="vt.value" :value="vt.value">
                {{ vt.label }}
              </option>
            </select>
          </div>
          <div class="form-row">
            <label>Value</label>
            <input type="text" v-model="newMetadata.value" placeholder="Initial value (optional)" />
          </div>
          <div class="form-row">
            <label>Unit</label>
            <input type="text" v-model="newMetadata.unit" placeholder="Unit (optional)" />
          </div>
          <div class="form-row checkbox-row">
            <label class="checkbox">
              <input type="checkbox" v-model="newMetadata.default" />
              <span>Default attribute</span>
            </label>
          </div>
        </div>
        <div class="pl-modal-footer">
          <button class="btn" @click="createNewMetadata">Add</button>
          <button class="btn-secondary" @click="closeAddKeyModal">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import axios from "axios";
import apiService from "@/services/apiService";
import { API_ENDPOINTS } from "@/services/config";
import { detachMdListFromEntity } from "@/services/mdlistBindingService";
import { confirmDelete } from "@/utils/confirmDelete";
import { buildMetadataEntityParams, normalizeMetadataKeyCollection, normalizeMetadataKeyValueCollection } from "@/utils/metadataTransforms";
import { getValueTypes, VALUE_TYPES, coerceValue, formatValue, getValueTypeLabel, valueToInputFormat, inputToStorageFormat } from "@/utils/valueTypeCoercion";
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";

export default {
  name: "MetadataListPanel",
  components: { Multiselect },
  props: {
    mdListId: { type: Number, required: true },
    assType: { type: String, required: true }, // 'experiment' | 'instrument' | 'material'
    assId: { type: Number, required: true },
  },
  data() {
    return {
      mdList: null,
      metadata: [],
      metadataSnapshot: [],
      nonDefaultKeys: [],
      selectedNonDefaultKey: "",
      newMetadata: { key: "", value: "", unit: "", value_type: VALUE_TYPES.STRING, default: false },
      showAddKeyModal: false,
      allMdLists: [],
      selectedMdListToAdd: "",
      isEditingAll: false,
      valueTypes: getValueTypes(),
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  mounted() {
    if (this.mdListId) {
      this.fetchAll();
    } else {
      this.fetchAllCandidateMdLists();
    }
  },
  watch: {
    mdListId(newVal) {
      if (newVal) {
        this.fetchAll();
      } else {
        this.allMdLists = [];
        this.selectedMdListToAdd = "";
        this.fetchAllCandidateMdLists();
      }
    },
  },
  methods: {
    usesDirectEntityMetadataListBinding() {
      return ["instrument", "material"].includes(this.assType);
    },

    itemKey(item) {
      return `md-${item.original_md_key_id || "key"}-${item.md_value_id || item.node_metadata_id || "value"}`;
    },

    formatMetadataValue(value, valueType) {
      return formatValue(value, valueType);
    },

    getTypeLabel(valueType) {
      return getValueTypeLabel(valueType);
    },

    valueToInputFormat(value, valueType) {
      return valueToInputFormat(value, valueType);
    },

    inputToStorageFormat(inputValue, valueType) {
      return inputToStorageFormat(inputValue, valueType);
    },

    normalizeFloatInput(value) {
      let normalized = String(value ?? "").replace(/,/g, ".");
      normalized = normalized.replace(/[^0-9.-]/g, "");
      normalized = normalized.replace(/(?!^)-/g, "");

      const firstDot = normalized.indexOf(".");
      if (firstDot !== -1) {
        normalized =
          normalized.slice(0, firstDot + 1) +
          normalized.slice(firstDot + 1).replace(/\./g, "");
      }

      return normalized;
    },

    canInsertDecimalPoint(currentValue, start, end) {
      const current = String(currentValue ?? "");
      const selected = current.slice(start, end);
      if (selected.includes(".")) {
        return true;
      }
      return !current.includes(".");
    },

    insertDecimalPoint(event, item) {
      const input = event.target;
      const current = String(item?.value ?? input?.value ?? "");
      const start = Number.isInteger(input?.selectionStart) ? input.selectionStart : current.length;
      const end = Number.isInteger(input?.selectionEnd) ? input.selectionEnd : current.length;

      if (!this.canInsertDecimalPoint(current, start, end)) {
        return;
      }

      const nextValue = `${current.slice(0, start)}.${current.slice(end)}`;
      item.value = this.normalizeFloatInput(nextValue);

      this.$nextTick(() => {
        const cursor = start + 1;
        input?.setSelectionRange?.(cursor, cursor);
      });
    },

    handleFloatBeforeInput(event, item) {
      if (event?.data !== ',' && event?.data !== '.') {
        return;
      }
      event.preventDefault();
      this.insertDecimalPoint(event, item);
    },

    handleFloatKeydown(event, item) {
      if (event.key !== ',' && event.key !== '.') {
        return;
      }
      event.preventDefault();
      this.insertDecimalPoint(event, item);
    },

    async fetchAll() {
      await Promise.all([
        this.fetchMetadata(),
        this.fetchNonDefaultKeys(),
      ]);
    },

    async fetchMetadata() {
      try {
        const mdList = await apiService.get(
          API_ENDPOINTS.mdlist(this.mdListId),
          { entity_type: this.assType },
          this.loggedInUser?.entra_id
        );
        this.mdList = mdList;

        await this.fetchKeyValuePairs();
      } catch (e) {
        if (e.response && e.response.status !== 404) {
          this.toast.error("Failed to fetch metadata!");
          console.error(e);
        }
      }
    },

    async fetchNonDefaultKeys() {
      try {
        const res = await axios.get(
          API_ENDPOINTS.nonDefaultKeys(this.mdListId),
          {
            params: buildMetadataEntityParams(this.assType, this.assId),
          }
        );
        this.nonDefaultKeys = normalizeMetadataKeyCollection(res.data);
      } catch (e) {
        console.warn("Failed to fetch non-default keys", e);
      }
    },

    async addExistingNonDefaultKey() {
      if (!this.selectedNonDefaultKey) {
        this.toast.error("Please select a key.");
        return;
      }
      try {
        const payload = {
          entra_id: this.loggedInUser?.entra_id,
          entity_type: this.assType,
          node_id: this.assId,
          metadata_list_id: this.mdListId,
          metadata_key_id: this.selectedNonDefaultKey.id,
          value: null,
        };

        await apiService.createResource(
          API_ENDPOINTS.mdvaluesWithKey,
          payload,
          this.loggedInUser?.entra_id
        );

        this.toast.success("Non-default key added.");
        this.selectedNonDefaultKey = "";
        await this.fetchMetadata();
        await this.fetchNonDefaultKeys();
      } catch (e) {
        this.toast.error("Failed to add key.");
        console.error(e);
      }
    },

    async fetchKeyValuePairs() {
      try {
        const response = await apiService.get(
          API_ENDPOINTS.keyValues(this.mdListId),
          buildMetadataEntityParams(this.assType, this.assId),
          this.loggedInUser?.entra_id
        );

        this.metadata = normalizeMetadataKeyValueCollection(response)
          .sort((left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0))
          .map((item) => ({
            ...item,
            isEditing: false,
          }));
      } catch (e) {
        this.toast.error("Failed to fetch key-value pairs!");
        console.error(e);
      }
    },

    async fetchAllCandidateMdLists() {
      try {
        const res = await axios.get(API_ENDPOINTS.mdlists, {
          params: { entity_type: this.assType },
        });
        this.allMdLists = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Failed to fetch available metadata lists", e);
        this.toast.error("Failed to fetch available metadata lists.");
      }
    },

    async attachMdListToEntity() {
      if (!this.selectedMdListToAdd) return;

      this.$emit("mdListCreated", this.selectedMdListToAdd);

      if (this.toast) this.toast.success("Metadata list selected.");
      this.selectedMdListToAdd = "";
    },

    enableEdit(index) {
      if (this.isEditingAll) return;
      this.metadata[index] = { ...this.metadata[index], isEditing: true };
    },
    cancelEdit(index) {
      this.metadata[index] = { ...this.metadata[index], isEditing: false };
    },

    enableEditAll() {
      this.metadataSnapshot = this.metadata.map((item) => ({ ...item }));
      this.isEditingAll = true;
      this.metadata = this.metadata.map((item) => ({ ...item, isEditing: true }));
    },

    cancelEditAll() {
      if (this.metadataSnapshot.length) {
        this.metadata = this.metadataSnapshot.map((item) => ({ ...item, isEditing: false }));
      } else {
        this.metadata = this.metadata.map((item) => ({ ...item, isEditing: false }));
      }
      this.metadataSnapshot = [];
      this.isEditingAll = false;
    },

    async persistMetadataItem(item) {
      let cleanedValue = item.value === "" ? null : item.value;

      if (cleanedValue !== null && item.value_type) {
        const coerceResult = coerceValue(cleanedValue, item.value_type);
        if (!coerceResult.valid) {
          throw new Error(`Value validation failed for ${item.name}: ${coerceResult.error}`);
        }
        cleanedValue = coerceResult.coercedValue;
      }

      const payload = {
        entra_id: this.loggedInUser?.entra_id,
        entity_type: this.assType,
        node_id: this.assId,
        metadata_list_id: this.mdListId,
        metadata_key_id: item.original_md_key_id,
        name: item.name,
        unit: item.unit || null,
        value_type: item.value_type || VALUE_TYPES.STRING,
        is_default: Boolean(item.is_default),
        value: cleanedValue,
      };

      await apiService.createResource(
        API_ENDPOINTS.mdvaluesWithKey,
        payload,
        this.loggedInUser?.entra_id
      );
    },

    async saveMetadata(index) {
  try {
    const item = this.metadata[index];
    await this.persistMetadataItem(item);

    this.metadata[index] = { ...item, isEditing: false };
  } catch (e) {
    this.toast.error(e.message || "Failed to save metadata!");
    console.error(e);
  }
},

    async saveAllMetadata() {
      try {
        for (const item of this.metadata) {
          await this.persistMetadataItem(item);
        }

        this.metadata = this.metadata.map((item) => ({ ...item, isEditing: false }));
        this.metadataSnapshot = [];
        this.isEditingAll = false;
        this.toast.success("Metadata values updated successfully.");
      } catch (e) {
        this.toast.error(e.message || "Failed to save all metadata values!");
        console.error(e);
      }
    },

    // modal controls + create new metadata
    openAddKeyModal() {
      this.newMetadata = { key: "", value: "", unit: "", value_type: VALUE_TYPES.STRING, default: false };
      this.showAddKeyModal = true;
    },
    closeAddKeyModal() {
      this.showAddKeyModal = false;
    },

    async createNewMetadata() {
      if (!this.newMetadata.key) {
        this.toast.error("Please fill in key field.");
        return;
      }
      try {
        // 1) Create MdKey on the template
        const keyData = {
          entity_type: this.assType,
          name: this.newMetadata.key,
          value_type: this.newMetadata.value_type,
          unit: this.newMetadata.unit,
          is_default: this.newMetadata.default,
          default_value: this.newMetadata.default ? (this.newMetadata.value || null) : null,
        };
        const createdKeyResponse = await apiService.createResource(
          API_ENDPOINTS.mdkeys(this.mdListId),
          keyData,
          this.loggedInUser?.entra_id
        );
        const createdKey = createdKeyResponse?.metadata_key || createdKeyResponse;

        // 2) Attach it to this entity and set initial value
        let mdvalue = this.newMetadata.value || null;

        // Validate and coerce value if provided
        if (mdvalue !== null) {
          const coerceResult = coerceValue(mdvalue, this.newMetadata.value_type);
          if (!coerceResult.valid) {
            this.toast.error(`Value validation failed: ${coerceResult.error}`);
            return;
          }
          mdvalue = coerceResult.coercedValue;
        }

        const payload = {
          entra_id: this.loggedInUser?.entra_id,
          entity_type: this.assType,
          node_id: this.assId,
          metadata_list_id: this.mdListId,
          metadata_key_id: createdKey.id,
          value: mdvalue,
        };

        await apiService.createResource(
          API_ENDPOINTS.mdvaluesWithKey,
          payload,
          this.loggedInUser?.entra_id
        );

        this.toast.success("Metadata added successfully!");
        this.closeAddKeyModal();
        await this.fetchMetadata();
      } catch (error) {
        this.toast.error("Failed to add metadata.");
        console.error(error);
      }
    },
    async deleteMdList() {
      try {
        const isConfirmed = await confirmDelete("Remove this metadata template from this entity?");
        if (!isConfirmed) return;

        if (!this.usesDirectEntityMetadataListBinding()) {
          await detachMdListFromEntity(
            this.assType,
            this.assId,
            this.mdListId,
            this.loggedInUser?.entra_id
          );
        }

        this.toast.success("Template removed.");

        // Reset component
        this.mdList = null;
        this.metadata = [];
        this.nonDefaultKeys = [];

        // Notify parent view to refresh
        this.$emit("mdListDetached");
        this.$emit("update");

      } catch (error) {
        this.toast.error("Failed to remove template.");
        console.error("MDList deletion failed:", error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss"; // ensures $primary-color is available

/* Card */
.mdlist-panel {
  border: 1px solid #e8e8ec;
  border-radius: 10px;
  padding: 14px;
  background: $secondary-color;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
}

/* Header */
.mdlist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.title { font-weight: 600; }
.header-actions {
  display: flex; gap: 8px; align-items: center;
}

/* Buttons */
.btn, .btn-secondary, .btn-ghost, .save-btn, .cancel-btn {
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: filter .2s ease, background-color .2s ease, color .2s ease, border-color .2s ease;
  border: none;
}

.btn {
  padding: 6px 12px;
  background: $primary-color;
  color: #fff;
}
.btn:hover { filter: brightness(0.95); }

.btn-secondary {
  padding: 6px 12px;
  background: lighten($primary-color, 36%);
  color: #222;
}
.btn-secondary:hover { filter: brightness(0.97); }

/* Outline / ghost style that still uses primary */
.btn-ghost {
  display: inline-flex; gap: 6px; align-items: center;
  padding: 6px 10px;
  background: transparent;
  color: $primary-color;
  border: 1px solid $primary-color;
}
.btn-ghost:hover { background: rgba($primary-color, 0.08); }

/* Toolbar */
.toolbar-row {
  display: flex; flex-wrap: wrap;
  gap: 12px; align-items: center;
  padding: 8px 10px;
  background: rgba($primary-color, 0.03);
  border: 1px solid rgba($primary-color, 0.08);
  border-radius: 8px;
  margin-bottom: 12px;
}
.add-existing-mdlist, .non-default-row {
  display: flex; gap: 8px; align-items: center;
}

/* Inputs / selects */
select, .edit-row input, .pl-modal-body input {
  padding: 6px 8px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  outline: none;
}
select:focus, .edit-row input:focus, .pl-modal-body input:focus {
  border-color: $primary-color;
  box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
}

.non-default-dropdown {
  padding: 6px 8px; border: 1px solid #ccc; border-radius: 6px;
}

/* List */
.kv-list { list-style: none; padding: 0; margin: 0; }
.metadata-item {
  margin-bottom: 8px; padding: 10px;
  border: 1px solid transparent; border-radius: 8px; cursor: pointer;
}
.metadata-item.editable:hover {
  border-color: rgba($primary-color, 0.25);
  background: rgba($primary-color, 0.06);
}
.editable-row { display: flex; align-items: center; gap: 6px; }
.metadata-key { font-weight: 600; }
.metadata-unit { color: gray; font-size: 0.9rem; }
.edit-row { display: flex; gap: 8px; align-items: center; }
.edit-row input { min-width: 220px; }

/* Inline save/cancel buttons themed to primary */
.save-btn, .cancel-btn {
  padding: 0.3rem 0.6rem;
}
.save-btn {
  background: $primary-color;
  color: #fff;
}
.cancel-btn {
  background: transparent;
  color: $primary-color;
  border: 1px solid $primary-color;
}
.cancel-btn:hover { background: rgba($primary-color, 0.08); }

/* Children */
.child-mdlists { margin-top: 12px; }
.nested-mdlist {
  padding-left: 20px;
  border-left: 2px solid lighten($primary-color, 40%);
  margin-top: 10px;
}

/* Modal */
.pl-modal-overlay {
  position: fixed; inset: 0; background: rgba(9,10,15,.45);
  display: grid; place-items: center; z-index: 2000; /* above navbars */
}
.pl-modal {
  width: 100%; max-width: 520px; background: #fff;
  border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.15);
  overflow: hidden;
}
.pl-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid #eee;
}
.pl-modal-close {
  background: transparent; border: 0; font-size: 22px; cursor: pointer; line-height: 1;
  color: $primary-color;
}
.pl-modal-close:hover { filter: brightness(0.9); }
.pl-modal-body { padding: 14px 16px; display: grid; gap: 10px; }
.pl-modal-footer {
  display: flex; gap: 8px; justify-content: flex-end; padding: 12px 16px; border-top: 1px solid #eee;
}
.form-row { display: grid; gap: 6px; }
.checkbox-row { margin-top: 4px; }
.checkbox { display: inline-flex; gap: 8px; align-items: center; }

/* Edit Row */
.edit-row {
  display: flex;
  gap: 8px;
  align-items: center;
  background: #f9f9f9;
  padding: 8px;
  border-radius: 6px;
}

.value-input,
.edit-row input,
.edit-row select {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  background: white;
}

.value-input:focus,
.edit-row input:focus,
.edit-row select:focus {
  outline: none;
  border-color: $primary-color;
  box-shadow: 0 0 0 2px rgba($primary-color, 0.1);
}
</style>


